<template>
  <v-dialog
    :model-value="modelValue"
    max-width="1100"
    scrollable
    class="ac-param-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card rounded="lg" class="ac-param-dialog__card">
      <div class="ac-param-dialog__head d-flex align-center flex-wrap ga-3 pa-4 pa-md-5 pb-2">
        <h3 class="text-h6 font-weight-semibold mb-0">空调参数设置</h3>
        <v-chip size="small" class="ac-param-dialog__chip text-none" variant="flat">
          {{ kindLabel }}空调
        </v-chip>
        <v-spacer />
        <v-btn icon variant="text" density="comfortable" aria-label="关闭" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pa-4 pa-md-5 pt-2 ac-param-dialog__body">
        <div
          v-for="(block, bi) in localBlocks"
          :key="block.id"
          class="ac-block rounded-lg pa-4 mb-4"
        >
          <div class="d-flex align-center ga-3 mb-4">
            <v-avatar size="28" color="success" class="text-caption font-weight-bold">{{ bi + 1 }}</v-avatar>
            <div class="d-flex flex-wrap align-end ga-4 flex-grow-1">
              <v-text-field
                v-model="block.startTime"
                label="开始时间"
                type="time"
                variant="outlined"
                density="comfortable"
                hide-details
                rounded="lg"
                class="ac-time-field"
              />
              <v-text-field
                v-model="block.endTime"
                label="结束时间"
                type="time"
                variant="outlined"
                density="comfortable"
                hide-details
                rounded="lg"
                class="ac-time-field"
              />
            </div>
          </div>

          <div class="ac-table-scroll">
            <div class="ac-table" role="table">
              <div class="ac-table__head" role="row">
                <div v-for="col in columns" :key="col.key" class="ac-table__th" role="columnheader">
                  {{ col.title }}
                </div>
              </div>
              <div v-for="(row, ri) in block.rows" :key="row._id" class="ac-table__row" role="row">
                <div v-for="col in columns" :key="col.key" class="ac-table__td" role="cell">
                  <div class="ac-stepper">
                    <v-btn
                      icon
                      variant="tonal"
                      size="x-small"
                      density="compact"
                      class="ac-stepper__btn"
                      @click="bump(block, row, col.key, -col.step)"
                    >
                      <v-icon size="16">mdi-minus</v-icon>
                    </v-btn>
                    <span class="ac-stepper__val">{{ formatNum(row[col.key]) }}</span>
                    <v-btn
                      icon
                      variant="tonal"
                      size="x-small"
                      density="compact"
                      class="ac-stepper__btn"
                      @click="bump(block, row, col.key, col.step)"
                    >
                      <v-icon size="16">mdi-plus</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex flex-wrap ga-4 mt-3">
            <button type="button" class="ac-link ac-link--danger text-none" @click="removeRow(block)">
              <span class="ac-link__prefix">−</span> 删除
            </button>
            <button type="button" class="ac-link ac-link--primary text-none" @click="addRow(block)">
              <span class="ac-link__prefix">+</span> 新增
            </button>
          </div>
        </div>

        <div class="d-flex flex-wrap ga-3 mt-2">
          <v-btn
            rounded="pill"
            variant="flat"
            class="ac-pill-btn ac-pill-btn--danger text-none"
            @click="removeBlock"
          >
            <span class="mr-1">−</span> 删除
          </v-btn>
          <v-btn
            rounded="pill"
            variant="flat"
            class="ac-pill-btn ac-pill-btn--success text-none"
            @click="addBlock"
          >
            <span class="mr-1">+</span> 新增
          </v-btn>
        </div>
      </v-card-text>

      <v-card-actions class="px-4 px-md-5 pb-4 pt-0">
        <v-spacer />
        <v-btn variant="text" rounded="lg" class="text-none" @click="close">取消</v-btn>
        <v-btn color="primary" variant="flat" rounded="lg" class="text-none" @click="confirm">确定</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch, computed } from 'vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  /** eblock | elink */
  kind: { type: String, default: 'eblock' },
  initialBlocks: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:modelValue', 'save'])

const kindLabel = computed(() => (props.kind === 'elink' ? 'eLink' : 'eBlock'))

const columns = [
  { key: 'startPowerKw', title: '起始工作功率(kW)', step: 0.5 },
  { key: 'endPowerKw', title: '结束工作功率(kW)', step: 0.5 },
  { key: 'coolStartC', title: '制冷开始温度(°C)', step: 0.5 },
  { key: 'coolEndC', title: '制冷结束温度(°C)', step: 0.5 },
  { key: 'heatStartC', title: '制热开始温度(°C)', step: 0.5 },
  { key: 'heatEndC', title: '制热结束温度(°C)', step: 0.5 },
]

function newRow() {
  return {
    _id: `r_${Math.random().toString(36).slice(2, 11)}`,
    startPowerKw: 0,
    endPowerKw: 0,
    coolStartC: 0,
    coolEndC: 0,
    heatStartC: 0,
    heatEndC: 0,
  }
}

function newBlock() {
  return {
    id: `b_${Math.random().toString(36).slice(2, 11)}`,
    startTime: '00:00',
    endTime: '23:59',
    rows: [newRow(), newRow()],
  }
}

const localBlocks = ref([])

function deepCloneBlocks(src) {
  if (!src?.length) return [newBlock()]
  return JSON.parse(
    JSON.stringify(
      src.map((b) => ({
        id: b.id,
        startTime: b.startTime,
        endTime: b.endTime,
        rows: b.rows.map((r) => ({
          ...r,
          _id: r._id || `r_${Math.random().toString(36).slice(2, 11)}`,
        })),
      })),
    ),
  )
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      localBlocks.value = deepCloneBlocks(props.initialBlocks)
    }
  },
)

function formatNum(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '0'
  if (Math.abs(n - Math.round(n)) < 1e-9) return String(Math.round(n))
  return n.toFixed(1).replace(/\.0$/, '')
}

function bump(_block, row, key, delta) {
  const col = columns.find((c) => c.key === key)
  const step = col?.step ?? 0.5
  const d = typeof delta === 'number' ? delta : step
  const next = Number(row[key]) + d
  const clamped = Math.max(-999, Math.min(999, next))
  row[key] = Math.round(clamped * 10) / 10
}

function addRow(block) {
  block.rows.push(newRow())
}

function removeRow(block) {
  if (block.rows.length <= 1) return
  block.rows.pop()
}

function addBlock() {
  localBlocks.value.push(newBlock())
}

function removeBlock() {
  if (localBlocks.value.length <= 1) return
  localBlocks.value.pop()
}

function close() {
  emit('update:modelValue', false)
}

function confirm() {
  const payload = localBlocks.value.map((b) => ({
    id: b.id,
    startTime: b.startTime,
    endTime: b.endTime,
    rows: b.rows.map(({ _id, ...rest }) => ({ ...rest })),
  }))
  emit('save', payload)
  emit('update:modelValue', false)
}
</script>

<style scoped lang="scss">
.ac-param-dialog__card {
  border: 1px solid #e9efff;
}

.ac-param-dialog__chip {
  background: #e8f5e9 !important;
  color: #2e7d32 !important;
  font-weight: 500;
}

.ac-param-dialog__body {
  max-height: min(72vh, 720px);
  overflow-y: auto;
}

.ac-block {
  border: 1px solid #e9efff;
  background: #fafbff;
}

.ac-time-field {
  max-width: 160px;
  min-width: 140px;
}

.ac-table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.ac-table {
  min-width: 720px;
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e9efff;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.ac-table__head,
.ac-table__row {
  display: grid;
  grid-template-columns: repeat(6, minmax(108px, 1fr));
}

.ac-table__head {
  background: #f4f6fc;
  border-bottom: 1px solid #e9efff;
}

.ac-table__th {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text, #444a6d);
  padding: 10px 8px;
  line-height: 1.35;
  border-right: 1px solid #edf2f6;
  &:last-child {
    border-right: none;
  }
}

.ac-table__row {
  border-bottom: 1px solid #edf2f6;
  &:last-child {
    border-bottom: none;
  }
}

.ac-table__td {
  padding: 8px;
  border-right: 1px solid #edf2f6;
  display: flex;
  align-items: center;
  justify-content: center;
  &:last-child {
    border-right: none;
  }
}

.ac-stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
}

.ac-stepper__btn {
  border-radius: 8px !important;
}

.ac-stepper__val {
  min-width: 36px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text, #444a6d);
}

.ac-link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  &--danger {
    color: #e53935;
  }
  &--primary {
    color: var(--color-primary, #2b57ee);
  }
}

.ac-link__prefix {
  font-weight: 700;
  margin-right: 2px;
}

.ac-pill-btn {
  text-transform: none;
  letter-spacing: 0;
  min-width: 120px;
  &--danger {
    background: #ffebee !important;
    color: #c62828 !important;
  }
  &--success {
    background: #e8f5e9 !important;
    color: #2e7d32 !important;
  }
}
</style>
